import { SwapMode } from './common/types/index.js';
import { deconstructPairId, fulfilledValues } from './utils.js';
import { ExchangeLimitsError } from './errors.js';
class ExchangeProviders {
    #allProviders;
    #logger;
    #providers;
    #assetAvailabilityService;
    constructor({ providers, logger, assetAvailabilityService }) {
        this.#allProviders = [...providers];
        this.#providers = providers;
        this.#logger = logger;
        this.#assetAvailabilityService = assetAvailabilityService;
    }
    getSources() {
        return this.#providers.flatMap((provider) => provider.sources);
    }
    async getQuotes(params) {
        const { from } = deconstructPairId(params.pairId);
        const expandFeeOpts = async (quote) => {
            const providerFee = quote.exchangeFeeData.feeOpts?.fee;
            const feeOpts = await params.getFeeOpts({
                amount: quote.fromAmount,
                assetId: from,
                fromAddress: params.fromAddress,
                isSendAll: params.isSendAll,
                ...quote.exchangeFeeData.feeOpts,
            });
            return {
                ...quote,
                exchangeFeeData: {
                    ...quote.exchangeFeeData,
                    feeOpts: {
                        ...feeOpts,
                        ...(providerFee ? { fee: providerFee } : {}),
                    },
                },
            };
        };
        const quotes = await Promise.all(this.#providers.map(async (provider) => {
            try {
                const isPairSupported = await provider.isPairSupported(params.pairId);
                if (!isPairSupported) {
                    return null;
                }
                const quote = await provider.getQuote(params);
                const requiresFeeOpts = !Object.hasOwn(quote.exchangeTxData, 'solanaTransactions');
                return requiresFeeOpts ? await expandFeeOpts(quote) : quote;
            }
            catch (err) {
                this.#logger?.debug(err);
                return null;
            }
        }));
        const notNullishQuotes = quotes.filter(Boolean);
        return notNullishQuotes.filter((quote, _index, array) => {
            if (quote.swapStrategy === 'chained' && array.length > 1) {
                this.#logger?.debug('skipping chained quote', quote);
                return false;
            }
            return true;
        });
    }
    async exchange(params) {
        const { provider, ...rest } = params;
        return provider.exchange(rest);
    }
    async getExchangeLimits(params) {
        const fulfilledResults = await fulfilledValues(this.#providers.map(async (provider) => {
            try {
                const isPairSupported = await provider.isPairSupported(params.pairId);
                if (!isPairSupported) {
                    return null;
                }
                const limits = await provider.getLimits(params);
                return limits.map((limit) => ({ ...limit, swapStrategy: provider.swapStrategy }));
            }
            catch {
                return null;
            }
        }), { logger: this.#logger });
        const providerLimits = fulfilledResults
            .flat()
            .filter((limit) => limit !== null);
        this.#logger?.debug(`Limits for pair ${params.pairId}: ${providerLimits
            .map((limit) => JSON.stringify(limit))
            .join(', ')}`);
        if (providerLimits.length === 0) {
            throw new ExchangeLimitsError({ message: `Couldn't get limits for ${params.pairId}` });
        }
        const limits = providerLimits.filter((limit, _index, array) => {
            if (limit.swapStrategy === 'chained' && array.length > 1) {
                this.#logger?.debug('skipping chained quote', limit);
                return false;
            }
            return true;
        });
        const initialLimit = limits.at(0);
        if (!initialLimit) {
            throw new ExchangeLimitsError({ message: `Couldn't get limits for ${params.pairId}` });
        }
        const bestLimits = limits.reduce((bestLimits, currentLimit) => {
            const newBestLimit = { ...bestLimits };
            if (currentLimit.ExactIn.min.lt(bestLimits.ExactIn.min)) {
                newBestLimit.ExactIn.min = currentLimit.ExactIn.min;
            }
            if (currentLimit.ExactIn.max.gt(bestLimits.ExactIn.max)) {
                newBestLimit.ExactIn.max = currentLimit.ExactIn.max;
            }
            if (currentLimit.ExactOut.min.lt(bestLimits.ExactOut.min)) {
                newBestLimit.ExactOut.min = currentLimit.ExactOut.min;
            }
            if (currentLimit.ExactOut.max.gt(bestLimits.ExactOut.max)) {
                newBestLimit.ExactOut.max = currentLimit.ExactOut.max;
            }
            if (currentLimit.expiry !== undefined) {
                newBestLimit.expiry = Math.min(newBestLimit.expiry ?? Infinity, currentLimit.expiry);
            }
            return newBestLimit;
        }, {
            [SwapMode.ExactIn]: {
                min: initialLimit.ExactIn.min,
                max: initialLimit.ExactIn.max,
            },
            [SwapMode.ExactOut]: {
                min: initialLimit.ExactOut.min,
                max: initialLimit.ExactOut.max,
            },
            ...(initialLimit.expiry === undefined ? {} : { expiry: initialLimit.expiry }),
        });
        this.#logger?.debug(`Best limits for pair ${params.pairId}: ${JSON.stringify(bestLimits)}`);
        return bestLimits;
    }
    getProviderByStrategy(nameOrProviderClass) {
        if (typeof nameOrProviderClass === 'string') {
            return this.#providers.find((provider) => provider.swapStrategy === nameOrProviderClass);
        }
        if (nameOrProviderClass?.swapStrategy) {
            return this.#providers.find((provider) => provider instanceof nameOrProviderClass);
        }
        return undefined;
    }
    setGeolocation(geolocation) {
        this.#providers.forEach((provider) => provider.setGeolocation(geolocation));
    }
    enableProvider(swapStrategy) {
        const provider = this.#allProviders.find((provider) => provider.swapStrategy === swapStrategy);
        if (!provider) {
            this.#logger?.warn(`Provider ${swapStrategy} doesn't exist`);
            return;
        }
        if (this.#providers.some((provider) => provider.swapStrategy === swapStrategy)) {
            this.#logger?.warn(`Provider ${swapStrategy} is already enabled`);
            return;
        }
        this.#providers.push(provider);
    }
    setTransactionPrivacyNetworks(swapStrategy, transactionPrivacyNetworks) {
        const provider = this.#allProviders.find((provider) => provider.swapStrategy === swapStrategy);
        if (!provider) {
            this.#logger?.warn(`Provider ${swapStrategy} doesn't exist`);
            return;
        }
        provider.setTransactionPrivacyNetworks(transactionPrivacyNetworks);
    }
    disableProvider(routeStrategy) {
        const index = this.#providers.findIndex((provider) => provider.swapStrategy === routeStrategy);
        if (index === -1) {
            this.#logger?.warn(`Provider ${routeStrategy} is already disabled`);
            return;
        }
        this.#providers.splice(index, 1);
    }
    async getSupportedFromAssetIds() {
        return this.#assetAvailabilityService.getSupportedFromAssetIds();
    }
    async getSupportedToAssetIds(from) {
        return this.#assetAvailabilityService.getSupportedToAssetIds(from);
    }
    async isAssetExchangeable(assetId) {
        return this.#assetAvailabilityService.isAssetExchangeable(assetId);
    }
    async startAssetAvailabilityRefresh(assetIds, enabledSources) {
        return this.#assetAvailabilityService.start(assetIds, enabledSources);
    }
    stopAssetAvailabilityRefresh() {
        this.#assetAvailabilityService.stop();
    }
    async updateAssetAvailability(assetIds, enabledSources) {
        return this.#assetAvailabilityService.update(assetIds, enabledSources);
    }
}
export { default as Providers } from './providers/index.js';
export { default as AssetsService } from './services/assets/index.js';
export { default as AssetAvailabilityService } from './services/asset-availability/index.js';
export { default as OrdersService } from './services/orders/index.js';
export { ExchangeError, ApproveTokensError, GetAllowanceError } from './errors.js';
export { default as BaseTransaction } from './transaction/BaseTransaction.js';
export { default as EvmTransaction } from './transaction/EvmTransaction.js';
export { default as SolanaTransaction } from './transaction/SolanaTransaction.js';
export { LOGS } from './constants.js';
export { SwapMode } from './common/types/index.js';
export default ExchangeProviders;
